/**
 * WoW Era Namespace Configuration
 *
 * Blizzard requires different API namespaces for different server types:
 * - Classic Era: static-classic1x-{region}, dynamic-classic1x-{region}, profile-classic1x-{region}
 * - TBC Anniversary: static-classicann-{region}, dynamic-classicann-{region}, profile-classicann-{region}
 */

import { RealmTypeFlags } from '../sharedTypes';

export enum EraNamespaceType {
  CLASSIC_ERA = 'classic1x',
  TBC_ANNIVERSARY = 'classicann'
}

/**
 * TBC Anniversary realms as of TBC 20th Anniversary launch.
 * These realms are also flagged in the database with `type & 64 != 0` (RealmTypeFlags.ANNIVERSARY_REALM).
 *
 * Note: The database flag is the source of truth. This list is only used when
 * the realm model is not available (e.g., during OAuth character fetching).
 */
export const TBC_ANNIVERSARY_REALMS: { slug: string; region: string }[] = [
  // US
  { slug: 'doomhowl', region: 'us' },
  { slug: 'dreamscythe', region: 'us' },
  { slug: 'maladath', region: 'us' }, // AU realm but in US region
  { slug: 'nightslayer', region: 'us' },
  // EU
  { slug: 'soulseeker', region: 'eu' },
  { slug: 'spineshatter', region: 'eu' },
  { slug: 'thunderstrike', region: 'eu' },
  // KR
  { slug: 'fengus-ferocity', region: 'kr' },
  { slug: 'moldars-moxie', region: 'kr' },
  { slug: 'slipkiks-savvy', region: 'kr' },
  // TW
  { slug: 'fengus-ferocity', region: 'tw' },
  { slug: 'moldars-moxie', region: 'tw' },
  { slug: 'slipkiks-savvy', region: 'tw' }
];

/**
 * Get the namespace type based on realm type flags.
 * This is the preferred method when you have access to the realm model.
 *
 * @param realmTypeFlags - The realm's type flags (realm.type)
 * @returns The appropriate namespace type
 */
export function getNamespaceTypeFromFlags(realmTypeFlags: number): EraNamespaceType {
  const isAnniversary = (realmTypeFlags & RealmTypeFlags.ANNIVERSARY_REALM) !== 0;
  return isAnniversary ? EraNamespaceType.TBC_ANNIVERSARY : EraNamespaceType.CLASSIC_ERA;
}

/**
 * Get the namespace type for a realm using the static realm list.
 * Use this only when you don't have access to the realm model (e.g., during OAuth).
 *
 * @param region - The WoW region (us, eu, kr, tw)
 * @param realmSlug - The realm's slug
 * @returns The appropriate namespace type
 */
export function getNamespaceTypeForRealm(region: string, realmSlug: string): EraNamespaceType {
  const isAnniversaryRealm = TBC_ANNIVERSARY_REALMS.some(
    (r) => r.region === region && r.slug === realmSlug
  );
  return isAnniversaryRealm ? EraNamespaceType.TBC_ANNIVERSARY : EraNamespaceType.CLASSIC_ERA;
}

/**
 * Get the profile namespace string for a given region and namespace type.
 *
 * @param region - The WoW region (us, eu, kr, tw)
 * @param namespaceType - The namespace type (classic1x or classicann)
 * @returns The profile namespace string (e.g., "profile-classic1x-us")
 */
export function getProfileNamespace(region: string, namespaceType: EraNamespaceType): string {
  return `profile-${namespaceType}-${region}`;
}

/**
 * Get the dynamic namespace string for a given region and namespace type.
 *
 * @param region - The WoW region (us, eu, kr, tw)
 * @param namespaceType - The namespace type (classic1x or classicann)
 * @returns The dynamic namespace string (e.g., "dynamic-classic1x-us")
 */
export function getDynamicNamespace(region: string, namespaceType: EraNamespaceType): string {
  return `dynamic-${namespaceType}-${region}`;
}

/**
 * Get the static namespace string for a given region and namespace type.
 *
 * @param region - The WoW region (us, eu, kr, tw)
 * @param namespaceType - The namespace type (classic1x or classicann)
 * @returns The static namespace string (e.g., "static-classic1x-us")
 */
export function getStaticNamespace(region: string, namespaceType: EraNamespaceType): string {
  return `static-${namespaceType}-${region}`;
}

/**
 * Get the render URL prefix for character thumbnails based on region and namespace type.
 *
 * @param region - The WoW region (us, eu, kr, tw)
 * @param namespaceType - The namespace type (classic1x or classicann)
 * @returns The render URL prefix (e.g., "//render.worldofwarcraft.com/classic1x-us/character/")
 */
export function getRenderUrlPrefix(region: string, namespaceType: EraNamespaceType): string {
  return `//render.worldofwarcraft.com/${namespaceType}-${region}/character/`;
}
